import { z, ZodError, type ZodType } from 'zod';

import { reverseColumnMapping } from './dataImport.helpers';
import {
  defaultDataImportParsers,
  type DataImportParserRegistry,
  normalizeParserExtension,
  type ParsedImportFile,
} from './dataImport.parsers';

export type { ParsedImportFile } from './dataImport.parsers';

export type ValidationErrorsTree = {
  errors: string[];
  properties?: Record<string, ValidationErrorsTree>;
  items?: ValidationErrorsTree[];
};

export function toValidationErrorsTree(error: unknown): ValidationErrorsTree {
  if (error instanceof ZodError) {
    return z.treeifyError(error);
  }

  if (error instanceof Error) {
    return { errors: [error.message || 'Unknown error'] };
  }

  return { errors: ['Unknown error'] };
}

export type ParseImportFileOptions = {
  /**
   * Extensions a caller means to allow, independent of which parsers happen
   * to be registered. Without this, a registry override that adds a parser
   * for one extension silently widens every other config sharing the
   * defaults, since parser presence was the only gate. Checked before parser
   * selection so it isn't merely a UI-level file-picker filter.
   */
  acceptedFileTypes?: string[];
  csvParseFailedMessage?: string;
  emptyFileMessage?: string;
  fileTooLargeMessage?: string;
  inconsistentFieldCountMessage?: string;
  /**
   * Upper bound in bytes, checked before any file content is read so an
   * oversized file is rejected without being loaded into memory and parsed.
   */
  maxFileSize?: number;
  unsupportedDelimiterMessage?: string;
  unsupportedFileTypeMessage?: string;
  /**
   * Replaces the extension→parser registry outright. Defaults to
   * {@link defaultDataImportParsers}. Callers that mean "the defaults plus
   * mine" pass an already-merged registry from `mergeDataImportParsers`.
   */
  parsers?: DataImportParserRegistry;
};

export async function parseImportFile(
  file: File,
  options: ParseImportFileOptions = {},
): Promise<ParsedImportFile> {
  if (options.maxFileSize !== undefined && file.size > options.maxFileSize) {
    throw new Error(
      options.fileTooLargeMessage?.trim() ||
        `File exceeds the maximum size of ${options.maxFileSize} bytes`,
    );
  }

  const fileExtension = file.name.includes('.')
    ? normalizeParserExtension(file.name.split('.').pop() ?? '')
    : '';

  const unsupportedFileTypeError = () =>
    new Error(
      options.unsupportedFileTypeMessage?.trim() ||
        `Unsupported file type ${fileExtension}`,
    );

  if (options.acceptedFileTypes) {
    const acceptedExtensions = new Set(
      options.acceptedFileTypes.map(normalizeParserExtension),
    );
    if (!acceptedExtensions.has(fileExtension)) {
      throw unsupportedFileTypeError();
    }
  }

  const parsers = options.parsers ?? defaultDataImportParsers;
  const parser = parsers[fileExtension];

  if (!parser) {
    throw unsupportedFileTypeError();
  }

  const parsed = await parser(file, {
    csvParseFailedMessage: options.csvParseFailedMessage,
    inconsistentFieldCountMessage: options.inconsistentFieldCountMessage,
    unsupportedDelimiterMessage: options.unsupportedDelimiterMessage,
  });

  if (parsed.headerFields.length === 0) {
    throw new Error(
      options.emptyFileMessage?.trim() ||
        'No header fields found, is the file empty?',
    );
  }

  return parsed;
}

type ValidateImportRowsInput = {
  columnMapping?: Record<string, string>;
  fileContent: Array<Record<string, unknown>>;
};

export function mapImportRows(
  fileContent: Array<Record<string, unknown>>,
  columnMapping?: Record<string, string>,
): Array<Record<string, unknown>> {
  const reversedColumnMapping = reverseColumnMapping(columnMapping);
  if (!reversedColumnMapping) return fileContent;

  return fileContent.map((row) =>
    Object.fromEntries(
      Object.entries(row).map(([key, value]) => [
        reversedColumnMapping[key] ?? key,
        value,
      ]),
    ),
  );
}

export function validateImportRows<TSchema extends ZodType>(
  input: ValidateImportRowsInput & { schema: TSchema },
): z.output<TSchema>;
export function validateImportRows(
  input: ValidateImportRowsInput & { schema?: ZodType },
): unknown;
export function validateImportRows({
  columnMapping,
  fileContent,
  schema,
}: ValidateImportRowsInput & { schema?: ZodType }): unknown {
  const rows = mapImportRows(fileContent, columnMapping);
  return schema?.parse(rows);
}
